import { defineComponent, h, type VNode, type Component, type Slot } from 'vue'
import { rehype } from 'rehype'

// ---------------------------------------------------------------------------
/**
 * Vue hypertext functions use a nested pattern. With rehype we can drill into
 * the nodes and build a mirrored hypertext rendering tree based on the HAST tree
 *
 * Special logic is provided to put the default slot where the ssg-children tag is found
 */

export interface ElementAttributes {
  [key: string]:
    | string
    | number
    | boolean
    | ElementAttributes
    | Array<ElementAttributes>
}

type VueNode = VNode | string

type Node =
  | {
      type: 'element'
      tagName: string
      properties: Record<string, unknown>
      children?: Node[]
    }
  | {
      type: 'text'
      value: string
      children?: Node[]
    }

/** Slots type for vue render functions */
type Slots = Record<string, Slot<any> | undefined>

function convertCamelToKebab(str: string) {
  return str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase()
}

/** Rehype properties are not always in the format we want for Vue */
export function formatProperties(properties: Record<string, unknown>) {
  const formattedProperties: Record<string, unknown> = {}

  const { className, ...rest } = properties

  formattedProperties.class = Array.isArray(className)
    ? className.join(' ')
    : ''

  Object.keys(rest).forEach((prop) => {
    const value = rest[prop]
    if (prop.startsWith('data')) {
      formattedProperties[convertCamelToKebab(prop)] = value
    } else {
      formattedProperties[prop] = value
    }
  })

  return formattedProperties
}

function renderNodeChildren(node: Node, slots?: Slots): VueNode[] | undefined {
  return node.children
    ?.map((n) => {
      if (n.type === 'element' && n.tagName === 'ssg-children') {
        // Check if the ssg-children has a name attribute that we can associate a slot with
        const slotName = n.properties?.name

        const slot =
          typeof slotName === 'string' ? slots?.[slotName] : slots?.default

        return slot?.()?.[0] ?? ''
      }

      return renderNode(n, slots)
    })
    .filter((v): v is VueNode => Boolean(v))
}

function renderNode(node: Node, slots?: Slots): VueNode | null {
  if (node.type === 'element') {
    return h(
      node.tagName,
      formatProperties(node.properties),
      renderNodeChildren(node, slots),
    )
  }
  if (node.type === 'text') {
    return node.value.trim()
  }
  return null
}

/**
 * Vue render functions cannot handle root HTML strings
 * Using rehype we can extract the first element and its properties
 * and return a plain object that can be used to render the component
 */
export function nestedRenderFunction(html: string, slots?: Slots) {
  const base = getHastTreeRoot(html)

  return base?.type === 'element'
    ? h(
        base.tagName,
        formatProperties(base.properties),
        renderNodeChildren(base, slots),
      )
    : h('')
}

/** Converts an html string render function into a Vue component */
export function toVueComponent<Attributes extends ElementAttributes>(
  /** The HTML string rendering function to convert */
  render: (props: Attributes) => string,
  /**
   * An example of the props to be used in the component
   * Warning: No prop validation is done, so the component will object with the keys specified here
   */
  propTemplate: {
    [x in keyof Attributes]:
      | StringConstructor
      | NumberConstructor
      | BooleanConstructor
  },
): Component<Attributes> {
  return defineComponent<Attributes>({
    props: Object.keys(propTemplate),
    setup(props, { slots }) {
      return () => nestedRenderFunction(render(props as Attributes), slots)
    },
  }) as Component<Attributes>
}

/** Functional rendering of an SVG string into a Vue component */
export function svgToVueComponent(svg: string): Component {
  return toVueComponent(() => svg, {})
}

/** Convert to a hast tree and return the first node */
export function getHastTreeRoot(html: string): Node | null {
  let node: Node | null = null

  rehype()
    .data('settings', { fragment: true })
    .use(() => {
      return (tree) => {
        if (!('children' in tree && Array.isArray(tree.children))) return

        const base = tree.children[0]
        if (base.type === 'element') {
          node = base
        }
      }
    })
    .processSync(html)

  return node
}

// ---------------------------------------------------------------------------

/**
 * Vue render functions cannot handle root HTML strings
 * Using rehype we can extract the first element and its properties
 * and return a plain object that can be used to render the component
 */
export function extractRootHtmlElement(html: string) {
  const root = {
    tagName: '',
    properties: {} as Record<string, any>,
    innerHTML: '',
  }

  const result = rehype()
    .data('settings', { fragment: true })
    .use(() => {
      return (tree) => {
        if (!('children' in tree && Array.isArray(tree.children))) return

        const first = tree.children[0]
        if (first.type === 'element') {
          root.tagName = first.tagName
          Object.assign(root.properties, first.properties)

          // Remove the first element from the tree
          tree.children = first.children
        }
      }
    })
    .processSync(html)
    .toString()

  root.properties.innerHTML = result
  return root
}
